import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { storage } from '@/lib/storage';
import {
  ConversationItem,
  ConversationDetail,
  MessageItem,
  BusinessHoursStatus,
} from './types';

const QUERY_KEYS = {
  conversations: 'customer-support-conversations',
  conversation: 'customer-support-conversation',
  activeConversation: 'customer-support-active-conversation',
  businessHours: 'customer-support-business-hours',
};

// =============================================================================
// CONVERSATIONS
// =============================================================================

export function useCustomerConversations(enabled = true) {
  return useQuery({
    queryKey: [QUERY_KEYS.conversations],
    queryFn: async () => {
      const response = await apiClient.get<ConversationItem[]>(
        '/customer/support-chat/conversations'
      );
      return response;
    },
    enabled,
  });
}

export function useCustomerConversation(conversationId: number | null) {
  // Ensure conversationId is always a number for consistent query keys
  const normalizedId = conversationId !== null 
    ? (typeof conversationId === 'string' ? parseInt(conversationId as unknown as string, 10) : conversationId)
    : null;
    
  return useQuery({
    queryKey: [QUERY_KEYS.conversation, normalizedId],
    queryFn: async () => {
      if (!normalizedId) return null;
      const response = await apiClient.get<ConversationDetail>(
        `/customer/support-chat/conversations/${normalizedId}`
      );
      return response;
    },
    enabled: !!normalizedId,
    staleTime: 0, // Always consider stale to ensure refetch works
  });
}

export function useActiveConversation() {
  return useQuery({
    queryKey: [QUERY_KEYS.activeConversation],
    queryFn: async () => {
      const response = await apiClient.get<ConversationDetail | null>(
        '/customer/support-chat/conversations/active'
      );
      // Explicitly return null if no active conversation (don't return undefined)
      return response ?? null;
    },
    staleTime: 0, // Always consider data stale to ensure fresh checks
    gcTime: 0, // Don't cache - always get fresh data to avoid showing stale conversations
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: true,
    placeholderData: null, // Start with null, not undefined
  });
}

export function useStartConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ subject, message, attachmentIds }: { subject?: string; message: string; attachmentIds?: number[] }) => {
      const response = await apiClient.post<ConversationDetail>(
        '/customer/support-chat/conversations',
        { subject, initialMessage: message, attachmentIds }
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.conversations] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.activeConversation] });
    },
  });
}

export function useSendCustomerMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      conversationId,
      content,
      attachmentIds,
    }: {
      conversationId: number;
      content: string;
      attachmentIds?: number[];
    }) => {
      const response = await apiClient.post<MessageItem>(
        `/customer/support-chat/conversations/${conversationId}/messages`,
        { content, attachmentIds }
      );
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.conversation, variables.conversationId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.activeConversation] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.conversations] });
    },
  });
}

export function useMarkMessagesRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (conversationId: number) => {
      await apiClient.post(`/customer/support-chat/conversations/${conversationId}/read`);
    },
    onSuccess: (_, conversationId) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.conversation, conversationId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.conversations] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.activeConversation] });
    },
  });
}

// =============================================================================
// CSAT
// =============================================================================

export function useSubmitCsatRating() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      conversationId,
      rating,
      comment,
    }: {
      conversationId: number;
      rating: number;
      comment?: string;
    }) => {
      await apiClient.post(`/customer/support-chat/conversations/${conversationId}/csat`, {
        rating,
        comment,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.conversation, variables.conversationId] });
    },
  });
}

// =============================================================================
// BUSINESS HOURS
// =============================================================================

export function useBusinessHoursStatus(enabled = true) {
  return useQuery({
    queryKey: [QUERY_KEYS.businessHours],
    queryFn: async () => {
      const response = await apiClient.get<BusinessHoursStatus>(
        '/customer/support-chat/business-hours'
      );
      return response;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled,
  });
}

// =============================================================================
// ATTACHMENTS
// =============================================================================

export function useUploadCustomerAttachments() {
  return useMutation({
    mutationFn: async ({ conversationId, files }: { conversationId: number; files: File[] }) => {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));

      // Use fetch directly for FormData uploads since apiClient.post sets Content-Type to application/json
      const token = apiClient.getAuthToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/customer/support-chat/conversations/${conversationId}/attachments`, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error('Failed to upload attachments');
      }
      
      return response.json() as Promise<{
        success: boolean;
        attachments: Array<{
          id: number;
          s3Key: string;
          fileName: string;
          fileType: string;
          fileSize: number;
          url: string;
        }>;
      }>;
    },
  });
}

export function useUploadAttachmentsForNewConversation() {
  return useMutation({
    mutationFn: async ({ files }: { files: File[] }) => {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));

      const token = apiClient.getAuthToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/customer/support-chat/attachments`, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error('Failed to upload attachments');
      }
      
      return response.json() as Promise<{
        success: boolean;
        attachments: Array<{
          id: number;
          s3Key: string;
          fileName: string;
          fileType: string;
          fileSize: number;
          url: string;
        }>;
      }>;
    },
  });
}

// =============================================================================
// ONLINE SUPPORT AGENTS
// =============================================================================

export interface OnlineSupportAgent {
  displayName: string;
  avatarUrl: string | null;
  status: 'online' | 'away' | 'busy' | 'offline';
}

export function useOnlineSupportAgents(enabled = true) {
  return useQuery({
    queryKey: ['online-support-agents'],
    queryFn: async () => {
      const response = await apiClient.get<OnlineSupportAgent[]>(
        '/customer/support-chat/online-agents'
      );
      return response;
    },
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: enabled ? 60 * 1000 : false, // Only refetch when enabled
    enabled,
  });
}

// =============================================================================
// COMBINED INIT (business hours + online agents in single request)
// =============================================================================

interface SupportChatInitData {
  businessHours: BusinessHoursStatus;
  onlineAgents: OnlineSupportAgent[];
}

export function useSupportChatInit(enabled = true) {
  return useQuery({
    queryKey: ['support-chat-init'],
    queryFn: async () => {
      const response = await apiClient.get<SupportChatInitData>(
        '/customer/support-chat/init'
      );
      return response;
    },
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: enabled ? 60 * 1000 : false, // Only refetch when enabled
    enabled,
  });
}
